/**
 * @module configGroups/ToolTipSetting
 * @description 浮窗配置（交互对齐 ChartsDrop ToolTipSetting）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('tooltipSetting')
 * 显隐：Config A itemsVisibility（ShowTotal / ShowShare / TooltipExtra）
 */

import React, { useMemo, useState, useCallback } from 'react';
import { shallowEqual, useIntl } from 'umi';
import { Bubble, Button, Modal, Switch } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import { useSettingObjectState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import TooltipExtraEditorModal from './TooltipExtraEditorModal';
import type { IDimensionOption, ITooltipExtraItem } from './types';
import { buildDimensionOptions, buildTableGroups } from './utils';
import './index.scss';

interface IToolTipSettingProps {
  visibleItems?: Record<string, boolean>;
}

const ToolTipSetting: React.FC<IToolTipSettingProps> = ({ visibleItems }) => {
  const { formatMessage } = useIntl();

  const dataTables = useQuerySelector(
    s => (s.dataTables as IGetTableInfoDto[]) ?? [],
    shallowEqual,
  );

  const { value, setValue } = useSettingObjectState('tooltipSetting');

  const showTotal = value.showTotal ?? true;
  const showShare = value.showShare ?? true;
  const tooltipExtra = value.tooltipExtra || [];

  const showTotalItem = visibleItems?.ShowTotal === true;
  const showShareItem = visibleItems?.ShowShare === true;
  const showExtraItem = visibleItems?.TooltipExtra === true;

  const [visible, setVisible] = useState(false);
  const [extraEditorMode, setExtraEditorMode] = useState<'add' | 'edit'>('add');
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [isIconHover, setIsIconHover] = useState(false);
  const hasExtraItems = !!tooltipExtra?.length;

  const tableGroups = useMemo(
    () => buildTableGroups(dataTables),
    [dataTables],
  );
  const dimensionOptions = useMemo<IDimensionOption[]>(
    () => buildDimensionOptions(dataTables),
    [dataTables],
  );

  const handleChange = useCallback(
    (newValue: typeof value) => {
      setValue(newValue);
    },
    [setValue],
  );

  const openExtraEditor = useCallback((modalMode: 'add' | 'edit') => {
    setExtraEditorMode(modalMode);
    setVisible(true);
  }, []);

  const handleDeleteExtra = useCallback(() => {
    handleChange({
      ...value,
      tooltipExtra: [],
    });
    setDeleteVisible(false);
  }, [handleChange, value]);

  return (
    <div className="dbl-chart-fields-tooltip">
      <ConfigCollapseCard
        title={formatMessage({ id: 'dbl_chart_tooltip' })}
        defaultActiveIds={[]}
      >
        <div className="dbl-chart-tooltip-setting">
          {showTotalItem && (
            <div className="dbl-chart-tooltip-setting-item dbl-chart-tooltip-setting-total">
              <div className="dbl-chart-tooltip-setting-label">
                {formatMessage({ id: 'dbl_chart_tooltip_total' })}
              </div>
              <div className="dbl-chart-tooltip-setting-controls">
                <Switch
                  value={showTotal}
                  onChange={bol => {
                    handleChange({
                      ...value,
                      showTotal: bol,
                    });
                  }}
                />
              </div>
            </div>
          )}
          {showShareItem && (
            <div className="dbl-chart-tooltip-setting-item dbl-chart-tooltip-setting-share">
              <div className="dbl-chart-tooltip-setting-label">
                {formatMessage({ id: 'dbl_chart_tooltip_share' })}
              </div>
              <div className="dbl-chart-tooltip-setting-controls">
                <Switch
                  value={showShare}
                  onChange={bol => {
                    handleChange({
                      ...value,
                      showShare: bol,
                    });
                  }}
                />
              </div>
            </div>
          )}
          {showExtraItem && (
            <div className="dbl-chart-tooltip-setting-extra">
              {!hasExtraItems ? (
                <div className="dbl-chart-tooltip-setting-extra-entry">
                  <span
                    className="dbl-link-style"
                    onClick={() => openExtraEditor('add')}
                  >
                    <span className="dbl-chart-tooltip-setting-extra-entry-add">
                      +
                    </span>
                    {formatMessage({ id: 'dbl_chart_tooltip_add' })}
                  </span>
                  <Bubble
                    dark
                    content={formatMessage({
                      id: 'dbl_chart_tooltip_extra_info_tip',
                    })}
                  >
                    <div
                      className="dbl-chart-tooltip-setting-extra-entry-info"
                      onMouseEnter={() => setIsIconHover(true)}
                      onMouseLeave={() => setIsIconHover(false)}
                    >
                      <SvgIcon
                        className="port-icon"
                        type={
                          isIconHover ? 'alert-circle-action' : 'alert-circle-m'
                        }
                      />
                    </div>
                  </Bubble>
                </div>
              ) : null}
              {hasExtraItems ? (
                <div className="dbl-chart-tooltip-setting-extra-preview-wrap">
                  <div className="dbl-chart-tooltip-setting-extra-preview-actions">
                    <span
                      className="dbl-link-style"
                      onClick={() => openExtraEditor('edit')}
                    >
                      {formatMessage({ id: 'dbl_common_edit' })}
                    </span>
                    <span
                      className="dbl-link-style"
                      onClick={() => setDeleteVisible(true)}
                    >
                      {formatMessage({
                        id: 'dbl_chart_tooltip_extra_clear_all',
                      })}
                    </span>
                  </div>
                  <div
                    className="dbl-chart-tooltip-setting-extra-preview"
                    onClick={() => openExtraEditor('edit')}
                  >
                    <div className="dbl-chart-tooltip-setting-extra-preview-content">
                      {tooltipExtra.map((item: ITooltipExtraItem, index: number) => {
                        const key = `${item.type}-${item.key || item.value}-${index}`;
                        if (item.type === 'field') {
                          return (
                            <span
                              key={key}
                              className="dbl-chart-tooltip-extra-field-tag"
                            >
                              <span className="dbl-chart-tooltip-extra-field-tag-label">
                                {item.label || item.value}
                              </span>
                            </span>
                          );
                        }
                        return <span key={key}>{item.value}</span>;
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </ConfigCollapseCard>

      <TooltipExtraEditorModal
        visible={visible}
        mode={extraEditorMode}
        tooltipExtra={tooltipExtra}
        tableGroups={tableGroups}
        dimensionOptions={dimensionOptions}
        onClose={() => setVisible(false)}
        onConfirm={(items: ITooltipExtraItem[]) => {
          handleChange({
            ...value,
            tooltipExtra: items,
          });
        }}
      />
      <Modal
        visible={deleteVisible}
        caption={formatMessage({ id: 'dbl_chart_tooltip_extra_delete_title' })}
        onClose={() => setDeleteVisible(false)}
      >
        <Modal.Body>
          {formatMessage({ id: 'dbl_chart_tooltip_extra_delete_confirm' })}
        </Modal.Body>
        <Modal.Footer>
          <Button type="primary" onClick={handleDeleteExtra}>
            {formatMessage({ id: 'dbl_common_confirm' })}
          </Button>
          <Button type="weak" onClick={() => setDeleteVisible(false)}>
            {formatMessage({ id: 'dbl_common_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default React.memo(ToolTipSetting);
